import { defineStore } from 'pinia';
import type { MenuItem, Order, OrderItem, OrderStatus } from '../shared/types';
import {
  createOrder as createOrderRequest,
  getOrders,
  updateOrderStatus as updateOrderStatusRequest,
} from '../services/apiClient';
import { connectRealtime as connectRealtimeClient } from '../services/realtime';

export const useOrdersStore = defineStore('orders', {
  state: () => ({
    // order list/status placeholder
    orders: [] as Order[],
    cartsByTable: {} as Record<string, OrderItem[]>,
  }),
  getters: {
    cartTotal: (state) => (tableId: string) => (state.cartsByTable[tableId] || []).reduce((sum, item) => sum + item.price * item.quantity, 0),
    cartItemsForTable: (state) => (tableId: string) => state.cartsByTable[tableId] || [],
  },
  actions: {
    async loadOrders() {
      try {
        const response = await getOrders();
        this.orders = response.orders;
      } catch (error) {
        console.warn('Failed to load orders from server', error);
      }
    },
    connectRealtime() {
      connectRealtimeClient(this);
    },
    pushOrder(order: Order) {
      const existing = this.orders.find((o) => o.id === order.id);
      if (!existing) {
        this.orders.push(order);
      }
    },
    replaceOrder(order: Order) {
      const existing = this.orders.find((o) => o.id === order.id);
      if (existing) {
        Object.assign(existing, order);
      } else {
        this.orders.push(order);
      }
    },
    addToCart(tableId: string, menuItem: MenuItem) {
      if (!tableId) {
        return;
      }

      const cartItems = this.cartsByTable[tableId] || [];
      const existing = cartItems.find((item) => item.menuItemId === menuItem.id);
      if (existing) {
        existing.quantity += 1;
      } else {
        cartItems.push({
          id: `${menuItem.id}-${Date.now()}`,
          menuItemId: menuItem.id,
          name: menuItem.name,
          price: menuItem.price,
          currency: menuItem.currency,
          quantity: 1,
          status: 'new',
        });
      }
      this.cartsByTable = { ...this.cartsByTable, [tableId]: cartItems };
    },
    updateCartItemQuantity(tableId: string, itemId: string, quantity: number) {
      const cartItems = this.cartsByTable[tableId] || [];
      const item = cartItems.find((cartItem) => cartItem.id === itemId);
      if (!item) return;
      if (quantity <= 0) {
        this.removeFromCart(tableId, itemId);
      } else {
        item.quantity = quantity;
        this.cartsByTable = { ...this.cartsByTable, [tableId]: cartItems };
      }
    },
    removeFromCart(tableId: string, itemId: string) {
      this.cartsByTable = {
        ...this.cartsByTable,
        [tableId]: (this.cartsByTable[tableId] || []).filter((cartItem) => cartItem.id !== itemId),
      };
    },
    async createOrder(tableId: string) {
      const cartItems = this.cartsByTable[tableId] || [];

      if (!cartItems.length) {
        return null;
      }

      const staffSession = (() => {
        try { return JSON.parse(localStorage.getItem('staff-session') || 'null'); } catch { return null; }
      })();
      const payload = {
        tableId,
        items: cartItems.map((item) => ({
          menuItemId: item.menuItemId,
          quantity: item.quantity,
          modifiers: item.modifiers?.map((modifier) => modifier.id) || [],
        })),
        staffUserId: staffSession?.id || null,
      };

      try {
        const response = await createOrderRequest(payload);
        this.orders.push(response.order);
        this.cartsByTable = {
          ...this.cartsByTable,
          [tableId]: [],
        };
        return response.order;
      } catch (error) {
        console.warn('Create order failed', error);
        throw error;
      }
    },
    clearCart(tableId?: string) {
      if (!tableId) {
        this.cartsByTable = {};
        return;
      }

      this.cartsByTable = {
        ...this.cartsByTable,
        [tableId]: [],
      };
    },
    async updateOrderStatus(orderId: string, newStatus: OrderStatus) {
      try {
        const response = await updateOrderStatusRequest(orderId, { status: newStatus });
        if (response.order) {
          const found = this.orders.find((o) => o.id === response.order.id);
          if (found) {
            Object.assign(found, response.order);
          }
        }
      } catch (error) {
        console.warn('Update order status failed', error);
        throw error;
      }
    },
  },
});
